import React, { useCallback, useState, useEffect, memo } from 'react';
import { api } from 'src/api';
import { removeURLParam } from 'src/components/AuthNew/utils';
import { Throbber } from '@lt/components';
import { useSearchParams } from 'src/components/AuthNew/hooks/useResetPasswordToken';
import styles from './confirmationProcessForm.module.css';

import { Title } from '../Title';
import { Description } from '../Description';
import type { SetFormType, SetSharedState } from '../../types';
import { AuthFormType } from '../../types';

interface Props {
  setFormType: SetFormType;
  onSuccess: (newEmail: string) => void;
  setSharedState: SetSharedState;
}

export const ConfirmationProcessForm: React.FC<Props> = memo(
  ({ setFormType, onSuccess, setSharedState }) => {
    const [loading, setLoading] = useState(true);
    const [error] = useState<string | null>(null);

    const { confirmation_token } = useSearchParams('confirmation_token');
    const { reset_password_token } = useSearchParams('reset_password_token');

    function resetParamToken(tokenType: string) {
      const currentURL = new URL(window.location.href);
      currentURL.searchParams.set(tokenType, '');
      window.history.pushState(null, document.title, currentURL.toString());
    }

    const checkResetPasswordToken = useCallback(async () => {
      try {
        const response = await api.getClientCheckResetPasswordToken({
          reset_password_token,
        });

        setSharedState((prev) => ({ ...prev, email: response?.email || '' }));
        if (response?.success) {
          setFormType(AuthFormType.CreatePassword);
        } else {
          resetParamToken('reset_password_token');
          setFormType(AuthFormType.InvalidToken);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, [reset_password_token]);

    const handleEmailConfirmation = useCallback(async () => {
      try {
        const response = await api.postClientConfirmEmail({
          confirmation_token,
        });
        removeURLParam('confirmation_token');

        if (response.success) {
          if (!reset_password_token && response.email) {
            onSuccess(response?.email);
          } else if (reset_password_token) {
            checkResetPasswordToken();
          }
        }
      } catch (e) {
        if (reset_password_token) {
          checkResetPasswordToken();
        } else {
          resetParamToken('confirmation_token');
          setFormType(AuthFormType.InvalidToken);
        }
        console.error(e);
      } finally {
        setLoading(true);
      }
    }, [confirmation_token]);

    useEffect(() => {
      if (confirmation_token) {
        handleEmailConfirmation();
      } else if (reset_password_token) {
        checkResetPasswordToken();
      }
    }, [
      handleEmailConfirmation,
      checkResetPasswordToken,
      confirmation_token,
      reset_password_token,
    ]);

    return (
      <>
        {loading && (
          <Throbber className={styles.throbber} size="30" embed color="black" />
        )}
        <Title>{loading ? 'Подождите, пожалуйста' : 'Ошибка'}</Title>
        <Description>{loading ? 'Подгружаем данные' : error}</Description>
      </>
    );
  },
);
